import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowLeft, FiMail, FiSave } from 'react-icons/fi';
import { toast } from 'react-toastify';
import EmailBodyEditor, { isEmailBodyEmpty } from '../../components/Common/EmailBodyEditor';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { mailAutoJobsAPI, emailTemplatesAPI, emailsAPI } from '../../services/api';
import {
  MERGE_TAGS,
  emptyMailAutoJobForm,
  jobToForm,
  buildMailAutoJobPayload
} from './mailAutoConfigShared';

const sectionCardStyle = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16
};

const MailAutoJobForm = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { activeCountry } = useCountry();
  const { canPerformAction } = useAuth();
  const isFranceCRM = String(activeCountry || '').toUpperCase() === 'FRANCE';
  const quillRef = useRef(null);

  const canSave = isEdit
    ? canPerformAction('mail_auto_config', 'update')
    : canPerformAction('mail_auto_config', 'create');

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [form, setForm] = useState(emptyMailAutoJobForm);

  useEffect(() => {
    if (!isFranceCRM) return;
    let cancelled = false;
    (async () => {
      try {
        const [tplRes, mbRes, tzRes] = await Promise.all([
          emailTemplatesAPI.list(),
          emailsAPI.getMailboxes(),
          mailAutoJobsAPI.getTimezoneOptions()
        ]);
        if (cancelled) return;
        const mbs = mbRes.data || [];
        const tzOpts = tzRes.data?.options || [];
        const defaultTz = tzRes.data?.default_timezone || 'Europe/Paris';
        setTemplates(tplRes.data || []);
        setMailboxes(mbs);
        setTimezoneOptions(tzOpts);

        if (!isEdit) {
          const defaultMailbox = mbs.find((m) => m.is_default) || mbs[0];
          setForm({
            ...emptyMailAutoJobForm,
            mailbox_id: defaultMailbox ? String(defaultMailbox.mailbox_id) : '',
            send_timezone: defaultTz
          });
          setLoading(false);
          return;
        }

        const jobRes = await mailAutoJobsAPI.get(id);
        if (cancelled) return;
        setForm(jobToForm(jobRes.data));
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.message || t('mailAutoConfig.loadFailed'));
          navigate('/mail-auto-config');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isFranceCRM, isEdit, id, navigate, t]);

  const insertMergeTag = (tagKey) => {
    const token = `{{${tagKey}}}`;
    const instance = quillRef.current;
    const quill = instance && typeof instance.getEditor === 'function' ? instance.getEditor() : null;
    if (quill) {
      const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
      quill.insertText(range.index, token, 'user');
      quill.setSelection(range.index + token.length, 0, 'user');
    } else {
      setForm((f) => ({ ...f, custom_body_html: `${f.custom_body_html || ''}${token}` }));
    }
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error(t('mailAutoConfig.nameRequired'));
      return false;
    }
    if (!form.recipient_students && !form.recipient_companies && !form.recipient_b2b_agencies) {
      toast.error(t('mailAutoConfig.recipientRequired'));
      return false;
    }
    if (!form.mailbox_id) {
      toast.error(t('mailAutoConfig.selectMailbox'));
      return false;
    }
    if (form.content_source === 'template' && !form.template_id) {
      toast.error(t('mailAutoConfig.templateRequired'));
      return false;
    }
    if (form.content_source === 'custom') {
      if (!String(form.custom_subject || '').trim()) {
        toast.error(t('mailAutoConfig.subjectRequired'));
        return false;
      }
      if (isEmailBodyEmpty(form.custom_body_html)) {
        toast.error(t('mailAutoConfig.bodyRequired'));
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!canSave || !validate()) return;
    try {
      setSaving(true);
      const payload = buildMailAutoJobPayload(form);
      if (isEdit) {
        await mailAutoJobsAPI.update(id, payload);
      } else {
        await mailAutoJobsAPI.create(payload);
      }
      toast.success(t('mailAutoConfig.saved'));
      navigate('/mail-auto-config');
    } catch (err) {
      toast.error(err.response?.data?.message || t('mailAutoConfig.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (!isFranceCRM) {
    return <Navigate to="/" replace />;
  }

  if (!canSave) {
    return <Navigate to="/mail-auto-config" replace />;
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-container" style={{ paddingBottom: 88 }}>
      <div className="page-header" style={{ marginBottom: 16 }}>
        <Link
          to="/mail-auto-config"
          className="btn btn-sm btn-outline"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}
        >
          <FiArrowLeft /> {t('mailAutoConfig.backToList')}
        </Link>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
          <FiMail />
          {isEdit ? t('mailAutoConfig.editJob') : t('mailAutoConfig.newJob')}
        </h1>
        <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
          {t('mailAutoConfig.formPageSubtitle')}
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 0 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, alignItems: 'start' }}>
          <div>
            <section style={sectionCardStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>{t('mailAutoConfig.sectionGeneral')}</h3>
              <div className="form-group">
                <label>{t('mailAutoConfig.jobName')}</label>
                <input
                  className="form-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  disabled={saving}
                />
              </div>
              <div className="form-group">
                <label>{t('mailAutoConfig.mailbox')}</label>
                <select
                  className="form-input"
                  value={form.mailbox_id}
                  onChange={(e) => setForm((f) => ({ ...f, mailbox_id: e.target.value }))}
                  disabled={saving}
                >
                  <option value="">{t('mailAutoConfig.selectMailbox')}</option>
                  {mailboxes.map((mb) => (
                    <option key={mb.mailbox_id} value={mb.mailbox_id}>
                      {mb.mailbox_name} ({mb.mailbox_email})
                    </option>
                  ))}
                </select>
              </div>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  disabled={saving}
                />
                {t('mailAutoConfig.active')}
              </label>
            </section>

            <section style={sectionCardStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>{t('mailAutoConfig.sectionSchedule')}</h3>
              <div className="form-group">
                <label>{t('mailAutoConfig.timezone')}</label>
                <select
                  className="form-input"
                  value={form.send_timezone}
                  onChange={(e) => setForm((f) => ({ ...f, send_timezone: e.target.value }))}
                  disabled={saving}
                >
                  {timezoneOptions.length === 0 && (
                    <option value={form.send_timezone}>{form.send_timezone}</option>
                  )}
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('mailAutoConfig.timezoneHint')}
                </p>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('mailAutoConfig.sendTimeAt')}</label>
                <input
                  type="time"
                  className="form-input"
                  value={form.send_time}
                  onChange={(e) => setForm((f) => ({ ...f, send_time: e.target.value }))}
                  disabled={saving}
                  style={{ maxWidth: 200 }}
                />
              </div>
            </section>

            <section style={sectionCardStyle}>
              <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>{t('mailAutoConfig.recipients')}</h3>
              <div style={{ display: 'grid', gap: 12 }}>
                <RecipientRow
                  checked={form.recipient_students}
                  onCheck={(v) => setForm((f) => ({ ...f, recipient_students: v }))}
                  label={t('mailAutoConfig.recipientStudents')}
                  days={form.delay_days_students}
                  onDays={(v) => setForm((f) => ({ ...f, delay_days_students: v }))}
                  daysLabel={t('mailAutoConfig.daysAfterEntry')}
                  disabled={saving}
                />
                <RecipientRow
                  checked={form.recipient_companies}
                  onCheck={(v) => setForm((f) => ({ ...f, recipient_companies: v }))}
                  label={t('mailAutoConfig.recipientCompanies')}
                  days={form.delay_days_companies}
                  onDays={(v) => setForm((f) => ({ ...f, delay_days_companies: v }))}
                  daysLabel={t('mailAutoConfig.daysAfterEntry')}
                  disabled={saving}
                />
                <RecipientRow
                  checked={form.recipient_b2b_agencies}
                  onCheck={(v) => setForm((f) => ({ ...f, recipient_b2b_agencies: v }))}
                  label={t('mailAutoConfig.recipientB2b')}
                  days={form.delay_days_b2b_agencies}
                  onDays={(v) => setForm((f) => ({ ...f, delay_days_b2b_agencies: v }))}
                  daysLabel={t('mailAutoConfig.daysAfterEntry')}
                  disabled={saving}
                />
              </div>
            </section>
          </div>

          <section style={{ ...sectionCardStyle, marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700 }}>{t('mailAutoConfig.emailContent')}</h3>
            <div style={{ display: 'flex', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  name="content_source"
                  checked={form.content_source === 'template'}
                  onChange={() => setForm((f) => ({ ...f, content_source: 'template' }))}
                  disabled={saving}
                />
                {t('mailAutoConfig.useTemplate')}
              </label>
              <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="radio"
                  name="content_source"
                  checked={form.content_source === 'custom'}
                  onChange={() => setForm((f) => ({ ...f, content_source: 'custom' }))}
                  disabled={saving}
                />
                {t('mailAutoConfig.writeCustom')}
              </label>
            </div>

            {form.content_source === 'template' ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('mailAutoConfig.template')}</label>
                <select
                  className="form-input"
                  value={form.template_id}
                  onChange={(e) => setForm((f) => ({ ...f, template_id: e.target.value }))}
                  disabled={saving}
                >
                  <option value="">{t('mailAutoConfig.selectTemplate')}</option>
                  {templates.map((tpl) => (
                    <option key={tpl.template_id} value={tpl.template_id}>{tpl.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="form-group">
                  <label>{t('mailAutoConfig.subject')}</label>
                  <input
                    className="form-input"
                    value={form.custom_subject}
                    onChange={(e) => setForm((f) => ({ ...f, custom_subject: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 8 }}>
                    {t('mailAutoConfig.mergeTags')}
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                    {MERGE_TAGS.map((tag) => (
                      <button
                        key={tag.key}
                        type="button"
                        className="btn btn-sm btn-outline"
                        disabled={saving}
                        onClick={() => insertMergeTag(tag.key)}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>
                <EmailBodyEditor
                  ref={quillRef}
                  value={form.custom_body_html}
                  onChange={(html) => setForm((f) => ({ ...f, custom_body_html: html }))}
                  minHeight={360}
                  themeMailbox
                  readOnly={saving}
                />
              </div>
            )}
          </section>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 40,
          background: 'var(--card-bg)',
          borderTop: '1px solid var(--border)',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.06)'
        }}
      >
        <Link to="/mail-auto-config" className="btn btn-outline" style={{ pointerEvents: saving ? 'none' : 'auto', opacity: saving ? 0.6 : 1 }}>
          {t('common.cancel')}
        </Link>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <FiSave /> {saving ? t('common.saving', 'Saving...') : t('common.save')}
        </button>
      </div>
    </div>
  );
};

function RecipientRow({ checked, onCheck, label, days, onDays, daysLabel, disabled }) {
  return (
    <div style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 8 }}>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600 }}>
        <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} disabled={disabled} />
        {label}
      </label>
      {checked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, marginLeft: 26 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{daysLabel}</span>
          <input
            type="number"
            min={0}
            max={3650}
            className="form-input"
            style={{ width: 100 }}
            value={days}
            onChange={(e) => onDays(e.target.value)}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

export default MailAutoJobForm;
